import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";
import { PRODUCT_GROUPS } from "@/data/content";

export default function Products() {
  return (
    <div data-testid="products-page">
      <PageHero
        overline="Our Products"
        title={<>Curated for <span className="text-gold-gradient italic">Craft.</span></>}
        subtitle="Premium plywood, decorative surfaces and precision hardware — everything an interior project demands, from a single trusted source."
        image="/photos/kitchen/2.webp"
      />

      {PRODUCT_GROUPS.map((g, gi) => (
        <section key={g.key} data-testid={`product-group-${g.key}`} className={`py-16 md:py-24 ${gi % 2 === 1 ? "bg-[#080808] border-y border-white/5" : ""}`}>
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className={`grid lg:grid-cols-2 gap-12 md:gap-16 items-center ${gi % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
              <Reveal className="lg:[direction:ltr]">
                <div className="group relative overflow-hidden border border-white/10 aspect-[5/4]">
                  <img src={g.image} alt={g.title} loading="lazy" className="img-lux h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/45 to-transparent" />
                  <span className="absolute top-5 left-5 font-serif-lux text-6xl text-[#e7c15a]/30">0{gi + 1}</span>
                </div>
              </Reveal>
              <div className="lg:[direction:ltr]">
                <Reveal>
                  <Overline>{`Category 0${gi + 1}`}</Overline>
                  <h2 className="font-serif-lux mt-5 text-4xl md:text-5xl leading-tight tracking-tight">{g.title}</h2>
                  <p className="mt-5 text-[#b8b8b8] leading-relaxed">{g.blurb}</p>
                </Reveal>
                <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {g.items.map((it, i) => (
                    <Reveal key={it} delay={i * 0.03}>
                      <div className="flex items-center gap-3 border-b border-white/8 pb-3" data-testid={`product-item-${g.key}-${i}`}>
                        <Check size={15} className="text-[#e7c15a] shrink-0" />
                        <span className="text-sm text-[#e0e0e0]">{it}</span>
                      </div>
                    </Reveal>
                  ))}
                </div>
                <Reveal delay={0.1}>
                  <Link to={`/contact?quote=1`} data-testid={`product-enquire-${g.key}`} className="mt-9 inline-flex items-center gap-2 border border-[#e7c15a] px-7 py-3.5 text-xs uppercase tracking-[0.2em] text-[#e7c15a] hover:bg-[#e7c15a] hover:text-[#050505] transition-colors">
                    Enquire About {g.title} <ArrowRight size={14} />
                  </Link>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="py-20 md:py-28 text-center">
        <div className="mx-auto max-w-2xl px-6">
          <Reveal>
            <h2 className="font-serif-lux text-3xl md:text-5xl tracking-tight">Looking for something specific?</h2>
            <p className="mt-5 text-[#b8b8b8]">We stock a wide range of interior products beyond this list — including screws, fasteners and architectural hardware. Catalogues coming soon.</p>
            <Link to="/contact?quote=1" className="mt-8 inline-flex items-center gap-2 bg-[#e7c15a] px-9 py-4 text-xs uppercase tracking-[0.2em] font-medium text-[#050505]">Request a Quote <ArrowRight size={15} /></Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
